import ImportExportIcon from '@mui/icons-material/ImportExport';
import { CircularProgress, IconButton, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { PATHS } from 'constants/paths';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { WellInfo, WellInfoForSite } from 'types/wellGuardian';

import styles from './SelectWell.module.scss';

type WellDetail = {
  wellName: string;
  wellViewId: string;
};

// Styling previously applied via withStyles now handled through sx / slotProps

interface WellHeaderProps {
  wellsForSite: WellInfoForSite[];
  wellsForSiteLoading: boolean;
  wellInfo: WellInfo | null;
}

const SelectWell: React.FunctionComponent<WellHeaderProps> = ({
  wellsForSite,
  wellsForSiteLoading,
  wellInfo,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSelectWellSort, setIsSelectWellSort] = useState<boolean>(true);

  const [wellDetail, setWellDetail] = useState<WellDetail>({
    wellName: '',
    wellViewId: '',
  });

  const wellsForSiteSorted = useMemo(() => {
    const copy = [...wellsForSite];
    copy.sort(
      (a, b) => (isSelectWellSort ? 1 : -1) * (a?.wellName ?? '').localeCompare(b?.wellName ?? ''),
    );

    return copy;
  }, [isSelectWellSort, wellsForSite]);

  const handleWellChange = useCallback(
    (well: WellInfo | null) => {
      if (!well) return;

      if (well?.wellViewId && wellInfo?.wellViewId) {
        navigate(location?.pathname.replace(wellInfo?.wellViewId, well?.wellViewId));
      } else {
        navigate(
          generatePath(PATHS.WELL_GUARDIAN_WELL_SINGLE_OVERVIEW, { tuwi: well?.wellViewId }),
        );
      }
    },
    [navigate, location?.pathname, wellInfo?.wellViewId],
  );

  useEffect(() => {
    if (
      wellsForSite.length === 0 ||
      wellsForSite.find(well => well?.wellViewId === wellInfo?.wellViewId)
    ) {
      setWellDetail({
        wellName: wellInfo?.wellName ?? '',
        wellViewId: wellInfo?.wellViewId ?? '',
      });
    } else {
      setWellDetail({
        wellName: wellsForSite[0]?.wellName ?? '',
        wellViewId: wellsForSite[0]?.wellViewId ?? '',
      });
      handleWellChange(wellsForSite[0]);
    }
  }, [handleWellChange, wellInfo, wellsForSite]);

  return (
    <Autocomplete
      id="select-well-id"
      data-testid="select-well-id"
      disableCloseOnSelect
      className={styles.selectWellRoot}
      classes={{
        endAdornment: styles.adornmentTextColor,
        paper: styles.autocompleteDropdown,
        option: styles.listOption,
      }}
      options={wellsForSiteSorted}
      autoHighlight
      getOptionLabel={option => (option.wellName ? option.wellName : '')}
      value={wellDetail}
      onChange={(_event, well) => handleWellChange(well)}
      isOptionEqualToValue={(option, value) => option.wellViewId === value.wellViewId}
      renderInput={params => (
        <div className={styles.selectWellDropdown}>
          {wellsForSiteLoading ? (
            <CircularProgress
              data-testid="select-well-loader"
              size={24}
              className={styles.selectWellLoading}
            />
          ) : (
            <TextField
              {...params}
              size="small"
              variant="standard"
              slotProps={{
                htmlInput: {
                  ...params.inputProps,
                  className: styles.selectWellText,
                },
              }}
              sx={{
                '& .MuiInputBase-input': { color: styles.white, fontWeight: 'bold' },
                // '& .MuiInputBase-root:before, & .MuiInputBase-root:after': {
                //   display: 'none',
                // },
                '& .MuiInput-root.MuiInputBase-sizeSmall .MuiInput-input': {
                  padding: ' 4px 4px 4px 0',
                },
              }}
            />
          )}
          <IconButton
            size="small"
            onClick={() => setIsSelectWellSort(prevState => !prevState)}
            data-testid="select-well-sort"
            className={styles.selectWellSort}
          >
            <ImportExportIcon fontSize="inherit" />
          </IconButton>
        </div>
      )}
    />
  );
};

export default SelectWell;
